// apps/web/src/features/clients/clientVersionImpact.ts
//
// What a historical restore would actually DO to the live aggregate (AC-11).
// Every rule below mirrors one write in the backend's `applySnapshot`
// (`apps/backend/src/modules/clients/aggregate-internal.ts`):
//
//   - the client's own name/comment/archive state is set to the recorded one,
//     while its manager/owner is deliberately kept;
//   - contacts and legal entities are matched BY ID: rows in both get the
//     recorded fields AND the recorded archive state, so a row that is active
//     now and archived in the snapshot is archived by the restore;
//   - a contact recorded in the snapshot but absent now is re-created under its
//     recorded id; an ACTIVE row absent from the snapshot is archived, while an
//     already-archived one simply stays archived and is not an effect;
//   - a legal entity recorded in the snapshot but absent now makes the server's
//     preflight refuse the whole restore.
//
// INVARIANT: nothing here is derived from text similarity, array position or
// bank metadata. The snapshot stores only the canonical requisites TEXT, so the
// preview never claims a bank set, its name/BIC/account or its history is
// restored — the server alone decides whether the text can be written safely.
import type { ClientDetail, ClientVersionSnapshot } from '@magimp/shared';

export type ClientRestoreRowKind = 'contact' | 'legal';

/**
 * One record the restore would touch, identified the way the operator sees it
 * TODAY: names are not unique, so a same-named sibling must still be tellable
 * apart, and a rename has to name the record before it names its future.
 */
export interface ClientRestoreRow {
  id: string;
  kind: ClientRestoreRowKind;
  /** Current name; the recorded one for a record that no longer exists. */
  name: string;
  /** Recorded name, only when the restore would rename the record. */
  recordedName?: string;
  /** Phone (contact) or INN (legal entity) — what separates same-named rows. */
  detail?: string;
}

/** Fields of the client row itself that the restore would rewrite. */
export type ClientRestoreRootChange = 'name' | 'comment' | 'archive' | 'unarchive';

export interface ClientRestoreImpact {
  /** Present in both, staying in the same archive state, with different data. */
  changed: ClientRestoreRow[];
  /** Active now, archived by the restore (recorded as archived, or absent). */
  archived: ClientRestoreRow[];
  /** Archived now, active in the snapshot — the restore brings them back. */
  restored: ClientRestoreRow[];
  /** Recorded contacts that no longer exist; the restore re-creates them. */
  recreated: ClientRestoreRow[];
  /** Changes to the client row itself. */
  rootChanges: ClientRestoreRootChange[];
  /**
   * Legal entities the snapshot records that no longer exist. The server's
   * preflight refuses the entire restore in that case — no partial write.
   */
  blocking: ClientRestoreRow[];
}

interface Row {
  id: string;
  name: string;
  /** Phone / INN: the field that tells two same-named records apart. */
  detail: string;
  archivedAt: string | null;
  /** Comparable business content, ignoring bookkeeping timestamps. */
  content: string;
}

function contactRow(c: {
  id: string;
  fullName: string;
  phone: string;
  comment: string | null;
  isPrimary: boolean;
  archivedAt: string | null;
}): Row {
  return {
    id: c.id,
    name: c.fullName,
    detail: c.phone,
    archivedAt: c.archivedAt,
    content: [c.fullName, c.phone, c.comment ?? '', String(c.isPrimary)].join('\0'),
  };
}

function legalRow(e: {
  id: string;
  name: string;
  inn: string;
  requisites: string;
  archivedAt: string | null;
}): Row {
  return {
    id: e.id,
    name: e.name,
    detail: e.inn,
    archivedAt: e.archivedAt,
    content: [e.name, e.inn, e.requisites].join('\0'),
  };
}

function asRestoreRow(row: Row, kind: ClientRestoreRowKind): ClientRestoreRow {
  const out: ClientRestoreRow = { id: row.id, kind, name: row.name };
  if (row.detail !== '') out.detail = row.detail;
  return out;
}

/**
 * A record that exists on both sides is named by what it is CALLED NOW, with
 * the recorded name carried separately: naming it by the snapshot's old name
 * does not tell the operator which of the rows in front of them that is.
 */
function asAffectedRow(live: Row, snap: Row, kind: ClientRestoreRowKind): ClientRestoreRow {
  const out = asRestoreRow(live, kind);
  if (snap.name !== live.name) out.recordedName = snap.name;
  return out;
}

function compareRows(
  live: readonly Row[],
  snapshot: readonly Row[],
  kind: ClientRestoreRowKind,
  impact: ClientRestoreImpact,
): void {
  const snapById = new Map(snapshot.map((r) => [r.id, r]));
  const liveById = new Map(live.map((r) => [r.id, r]));

  for (const row of live) {
    const snap = snapById.get(row.id);
    if (!snap) {
      // Absent from the snapshot: an active row is archived; an already
      // archived one keeps its state and is not an effect worth announcing.
      if (row.archivedAt === null) impact.archived.push(asRestoreRow(row, kind));
      continue;
    }
    if (row.archivedAt === null && snap.archivedAt !== null) {
      impact.archived.push(asAffectedRow(row, snap, kind));
      continue;
    }
    if (row.archivedAt !== null && snap.archivedAt === null) {
      impact.restored.push(asAffectedRow(row, snap, kind));
      continue;
    }
    if (row.content !== snap.content) impact.changed.push(asAffectedRow(row, snap, kind));
  }

  for (const snap of snapshot) {
    if (liveById.has(snap.id)) continue;
    // Contacts are re-created under the recorded id; a missing legal entity
    // makes the server refuse the restore instead.
    if (kind === 'contact') impact.recreated.push(asRestoreRow(snap, kind));
    else impact.blocking.push(asRestoreRow(snap, kind));
  }
}

/**
 * @aiPurpose describe the effect of restoring `snapshot` onto `live`, as the
 *   backend would apply it.
 * @aiInvariant identities come from the recorded/live rows themselves; the
 *   helper never infers bank-set identity, and it never reports an effect the
 *   backend does not perform (owner/manager, bank metadata, bank history).
 */
export function clientRestoreImpact(
  live: ClientDetail,
  snapshot: ClientVersionSnapshot,
): ClientRestoreImpact {
  const impact: ClientRestoreImpact = {
    changed: [],
    archived: [],
    restored: [],
    recreated: [],
    rootChanges: [],
    blocking: [],
  };

  compareRows(live.contacts.map(contactRow), snapshot.contacts.map(contactRow), 'contact', impact);
  compareRows(
    live.legalEntities.map(legalRow),
    snapshot.legalEntities.map(legalRow),
    'legal',
    impact,
  );

  if (live.name !== snapshot.name) impact.rootChanges.push('name');
  if ((live.comment ?? '') !== (snapshot.comment ?? '')) impact.rootChanges.push('comment');
  if (live.archivedAt === null && snapshot.archivedAt !== null) impact.rootChanges.push('archive');
  if (live.archivedAt !== null && snapshot.archivedAt === null) {
    impact.rootChanges.push('unarchive');
  }

  return impact;
}

/** True when the restore would leave the aggregate exactly as it is. */
export function isEmptyRestoreImpact(impact: ClientRestoreImpact): boolean {
  return (
    impact.changed.length === 0 &&
    impact.archived.length === 0 &&
    impact.restored.length === 0 &&
    impact.recreated.length === 0 &&
    impact.rootChanges.length === 0
  );
}
